import React from 'react';
import I18n from 'i18n-js';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { RCCard, RCFlex, RCIcon, RCText, useMediaQuery, buttonClassName } from '@reverbdotcom/cadence/components';
import { ArrowsClockwiseIcon, GlobeIcon, SparkleIcon, NudgeBoxIcon } from '@reverbdotcom/cadence/icons/react';
import { Paths } from './shared/url_helpers';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';

const COMPONENT_NAME = 'SellToFund';

type Variant = 'logged-out-homepage';

interface Props {
  variant?: Variant;
}

function SellToFundCTA({ isMobile, isTablet }: {
  isMobile: boolean;
  isTablet?: boolean;
}) {
  const clickEvent = {
    eventName: MParticleEventName.ClickedComponent,
    componentName: COMPONENT_NAME,
  };

  return (
    <CoreLink
      to={Paths.sellFormSearch.expand({})}
      clickEvent={clickEvent}
      className={buttonClassName({
        variant: 'filled',
        size: isMobile ? 'medium' : (isTablet ? 'medium' : 'large'),
        fullWidth: isMobile,
      })}
    >
      {I18n.t('discovery.sellToFund.cta')}
    </CoreLink>
  );
}

function ValuePropsSection({ isMobile }: { isMobile: boolean }) {
  const valueProps = [
    { icon: <GlobeIcon />, title: 'valuePropOne', description: 'valuePropOneDescription' },
    { icon: <SparkleIcon />, title: 'valuePropTwo', description: 'valuePropTwoDescription' },
    { icon: <NudgeBoxIcon />, title: 'valuePropThree', description: 'valuePropThreeDescription' },
  ];

  return (
    <div
      className="bd-radius-lg d-flex fx-dir-col gap-8 mobile-gap-4 padding-8 tablet-padding-6 mobile-padding-4"
      style={{ backgroundColor: 'var(--rc-color-palette-teal-100)' }}
    >
      {valueProps.map(({ icon, title, description }, index) => (
        <div key={title} className={`d-flex gap-4 ${index > 0 ? 'mt-4' : ''}`}>
          <div>
            <RCIcon size={{ desktop: 9, mobile: 7 }} icon={icon} />
          </div>
          <div className="d-flex fx-dir-col gap-2">
            <RCText.Utility size={isMobile ? '450' : '500'} weight="bold">
              {I18n.t(`discovery.sellToFund.${title}`)}
            </RCText.Utility>
            <RCText.Utility size={isMobile ? '400' : '450'}>
              {I18n.t(`discovery.sellToFund.${description}`)}
            </RCText.Utility>
          </div>
        </div>
      ))}
    </div>
  );
}

function SellToFundOrderConfirmation({ isMobile }: { isMobile: boolean }) {
  return (
    <RCCard>
      <div className="padding-6 mobile-padding-4 color-primary">
        <RCFlex
          gap={{ desktop: '4', mobile: '6' }}
          justify="space-between"
          direction={{ mobile: 'col' }}
          align={{ desktop: 'center', mobile: 'stretch' }}
        >
          <RCFlex gap={{ desktop: '4', mobile: '2' }} direction={{ mobile: 'col' }}>
            <div className="fx-align-self-start">
              <RCIcon size={7} icon={<ArrowsClockwiseIcon />} />
            </div>
            <RCFlex gap={{ desktop: '2', mobile: '1' }} direction="col">
              <RCText.Utility size={isMobile ? '450' : '500'} weight="bold">
                {I18n.t('discovery.sellToFund.title')}
              </RCText.Utility>
              <RCText.Utility size={isMobile ? '400' : '450'} weight="regular">
                {I18n.t('discovery.sellToFund.subtitle')}
              </RCText.Utility>
            </RCFlex>
          </RCFlex>
          <div className="height-100">
            <SellToFundCTA isMobile={isMobile} />
          </div>
        </RCFlex>
      </div>
    </RCCard>
  );
}

function SellToFundLoggedOutHomepage({ isMobile, isTablet }: { isMobile: boolean; isTablet: boolean }) {
  return (
    <div className="site-wrapper gap-row mb-gap-row">
      <div
        className="bd-radius-lg d-flex fx-justify-between mobile-fx-dir-col mobile-padding-4 tablet-padding-8 padding-12 gap-8 mobile-gap-0"
        style={{ backgroundColor: 'var(--rc-color-palette-teal-300)' }}
      >
        <div style={{ maxWidth: '700px' }}>
          <div className="pb-4">
            <RCIcon size={{ desktop: 9, mobile: 7 }} icon={<ArrowsClockwiseIcon />} />
          </div>
          <RCText.Title size={isMobile ? '600' : '1200'}>
            {I18n.t('discovery.sellToFund.title')}
          </RCText.Title>
          <div className="ptb-2 mobile-pb-8">
            {
              isMobile ?
                <RCText.Utility size="450">
                  {I18n.t('discovery.sellToFund.subtitle')}
                </RCText.Utility> :
                <RCText.Title size="700" weight="regular">
                  {I18n.t('discovery.sellToFund.subtitle')}
                </RCText.Title>
            }
          </div>
          {!isMobile && (
            <div className="mt-8">
              <SellToFundCTA isMobile={isMobile} isTablet={isTablet} />
            </div>
          )}
        </div>
        <div style={{ maxWidth: '650px' }}>
          <ValuePropsSection isMobile={isMobile} />
          {isMobile && (
            <div className="mt-8">
              <SellToFundCTA isMobile={isMobile} isTablet={isTablet} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SellToFund({ variant }: Props) {
  const isMobile = useMediaQuery('mobile');
  const isTablet = useMediaQuery('tablet');

  if (variant === 'logged-out-homepage') {
    return <SellToFundLoggedOutHomepage isMobile={isMobile} isTablet={isTablet} />;
  }

  return <SellToFundOrderConfirmation isMobile={isMobile} />;
}
